import React from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/subpage.css";
import "../../Styles/pages/chros.css";
import useReveal from "./useReveal";

export default function CHROs() {
  const ref = useReveal();

  return (
    <div className="page ann-subpage" ref={ref}>
      <section className="sub-hero" id="top">
        <div className="frame">
          <Link to="/" className="back">← Ann · Hiring intelligence</Link>
          <span>For CHROs &amp; people leaders</span>
        </div>
        <span className="eye">For CHROs &amp; people leaders</span>
        <h1>Hiring you can <em>stand behind</em>, the day someone asks how<span className="period" /></h1>
        <p className="lede-xl">
          Your hiring decisions get scrutinised — by the board, by candidates, by regulators, by your future self. <b>"Trust me, we just knew" is not a defence.</b> Ann makes every decision structured, comparable, and auditable: an evidence trail behind every verdict.
        </p>
        <div className="ctas">
          <Link to="/contact-us" className="btn btn-primary btn-lg">Book a demo</Link>
          <Link to="/how-it-works" className="btn btn-secondary btn-lg">How Ann works</Link>
        </div>
      </section>

      {/* THE EXPOSURE */}
      <section className="sub-section">
        <div className="cols-split">
          <div className="sub-head" style={{ marginBottom: 0 }}>
            <span className="eye">The exposure</span>
            <h2 className="sub-h2">Decision quality is now a <em>governance</em> question.</h2>
          </div>
          <div className="prose">
            <p className="big">Hiring used to be judged by outcomes alone. Now it's judged by process — and the process has to be one you can produce on demand.</p>
            <p>Regulators are writing rules for automated hiring; boards ask how senior decisions were made; candidates challenge rejections. The honest answer at most companies — a skim, a panel, a feeling in the room — survives none of it. <b>The risk isn't just a bad hire. It's a good decision you can't defend.</b></p>
            <p>Ann changes what a decision is made of: cited evidence, a consistent rubric, a verdict anyone would reach from the same facts — and a trail that's there when someone asks.</p>
          </div>
        </div>
      </section>

      {/* DEFENSIBILITY */}
      <section className="sub-section bone">
        <div className="cols-split">
          <div>
            <span className="eye" style={{ display: "block", marginBottom: 20 }}>What "defensible" looks like</span>
            <h2 className="sub-h2">Every verdict, with its <em>trail</em>.</h2>
            <p className="prose" style={{ marginTop: 8 }}>Ann doesn't ask anyone to take a score on faith. Each decision carries its evidence, the rubric it was scored against, and who reviewed it. <b>Reproducible, comparable, exportable</b> — when the question comes, the answer is already on the page.</p>
          </div>
          <div className="trail">
            <div className="t">Decision record · ROL-2049</div>
            <div className="ev"><span className="tk">RoleDNA</span><span className="lb">Criteria approved by hiring manager</span><span className="st">logged</span></div>
            <div className="ev"><span className="tk">Evidence</span><span className="lb">8 sources cited per candidate</span><span className="st">linked</span></div>
            <div className="ev"><span className="tk">Rubric</span><span className="lb">Same scoring across the slate</span><span className="st">applied</span></div>
            <div className="ev"><span className="tk">Review</span><span className="lb">Human verdict + sign-off</span><span className="st">recorded</span></div>
          </div>
        </div>
      </section>

      {/* THREE GUARANTEES */}
      <section className="sub-section">
        <div className="sub-head">
          <span className="eye">What you get</span>
          <h2 className="sub-h2">Structured. Comparable. <em>Auditable</em>.</h2>
        </div>
        <div className="cols-3">
          <div className="point">
            <div className="num">01 · Structured</div>
            <h3>The same bar, <em>org-wide</em>.</h3>
            <p>Every role scored against criteria you approve — not the mood of the panel. Consistency you can point to across teams, levels, and quarters.</p>
          </div>
          <div className="point">
            <div className="num">02 · Fair by design</div>
            <h3>Judged on <em>evidence</em>, nothing else.</h3>
            <p>Ann scores against the role — not names, photos, or school-as-proxy. Every score is explainable, every evaluation reviewable, the methodology documented.</p>
          </div>
          <div className="point">
            <div className="num">03 · Auditable</div>
            <h3>An answer <em>on demand</em>.</h3>
            <p>A complete trail behind every decision — exportable for the board, a regulator, or a candidate who asks why. The work is already done when the question lands.</p>
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="sub-section bone">
        <div className="sub-head"><span className="eye">What changes</span><h2 className="sub-h2">Confidence that scales past you.</h2></div>
        <div className="metrics">
          <div className="m"><div className="v">100%</div><div className="k">decisions with a<br />full evidence trail</div></div>
          <div className="m"><div className="v">1 bar</div><div className="k">held across every<br />team and level</div></div>
          <div className="m"><div className="v">on demand</div><div className="k">audit export for board<br />or regulator</div></div>
        </div>
        <p className="prose" style={{ marginTop: 28, fontSize: 13, color: "var(--ink-4)", fontFamily: "var(--f-mono)", letterSpacing: ".04em" }}>// Ann supports your governance; it does not constitute legal or compliance advice.</p>
      </section>

      {/* PROOF */}
      <section className="sub-section dark">
        <span className="eye" style={{ display: "block", marginBottom: 32 }}>From a CHRO</span>
        <p className="pquote">"For the first time, when the board asks how we chose, I can <span className="cy">show them</span> — not just reassure them."</p>
        <p className="pattrib"><span className="nm">CHRO</span> · regulated financial services · 4,000 employees</p>
      </section>

      {/* CTA */}
      <section className="sub-cta">
        <span className="eye">See the evidence trail</span>
        <h2>Make every hire <em>defensible</em>.</h2>
        <p className="sub">Fifteen minutes. We'll show you the decision record Ann produces — and what it looks like when someone asks how you chose.</p>
        <div className="ctas">
          <Link to="/contact-us" className="btn btn-primary btn-lg">Book a demo</Link>
          <Link to="/why-ann" className="btn btn-secondary btn-lg">Why Ann</Link>
        </div>
      </section>
    </div>
  );
}
